<template>
  <div>
    <SiteAlerts :site="site" :isProcessing="isProcessing" />
    <b-toast id="is-processing" variant="primary" solid no-auto-hide is-status no-close-button body-class="d-none">
      <template #toast-title>
        <div class="d-flex flex-grow-1 align-items-baseline">
          <b-spinner small />
          <strong class="mr-auto ml-2">Crawling your site</strong>
          <small class="text-muted mr-2">{{queueCompleted}} / {{queueDepth}}</small>
        </div>
      </template>
    </b-toast>
    <router-view
      :isProcessing="isProcessing"
      :siteTraffic="siteTraffic"
      :queue="queue.queues || []"
      @refresh="refresh()"
      @open="$emit('open')"
    />
  </div>
</template>
<style lang="scss">
.list-pane {
  width: 100%;
  position: relative;
  .bg-light {
    border-bottom: 1px solid var(--light-gray)
  }
}
.has-detail-pane {
  display: flex;
  height: calc(100vh - 95px);
  flex-direction: row;
  .list-pane {
    width: 500px;
    overflow-y: scroll;
  }
  .detail-pane {
    overflow-y: scroll;
    position: relative;
    z-index: 0;
  }
}
.detail-pane {
  width: calc(100% - 500px);
}


.has-detail-pane {
  .list-pane {
    .table {
      th {
        position: static;
      }
    }
  }
}
.table-shortcuts {
  position: absolute;
  top: 0;
  right: 5px;
}
#pages-table {
  border-top: 0;
  position: relative;
  background-color: #fff;
  th {
    border: 0;
  }
  td:first-child,
  th:first-child {
    padding-left: 1rem;
  }
  td:last-child {
    padding-right: 1rem !important;
  }
  th:last-child {
    padding-right: 1.75rem !important;
    background-position: right 0.75rem center !important;
  }
  .table-selected {
    background-color: mix(white, #00b3bc, 90%);
  }
  .hover-show {
    visibility: hidden;
  }
  .table-hovered, tr:hover:not(.disabled) {
    background-color: mix(white, #00b3bc, 95%);
    cursor: pointer;
    .disable-links {
      cursor: default;
    }
    .hover-show {
      visibility: visible;
    }
  }
}
</style>
<script>
import SiteAlerts from '@/components/SiteAlerts'
import QUEUE_QUERY from '@/graphql/QueueQuery.gql'
import SITE_QUERY from '@/graphql/SiteQuery.gql'
import GSC_SITE_TRAFFIC_QUERY from '@/graphql/GscSiteTrafficQuery.gql'
import {sumBy, isEmpty, omit, get, toNumber} from 'lodash'

const MIN_COUNT = 5

export default {
  components: { SiteAlerts },
  data() {
    return {
      queue: {
        queues: [],
        tracks: [],
      },
      site: {},
      changedSite: false,
      lastQueueDate: null,
      isDuda: false,
    }
  },
  
  apollo: {
    queue: {
      query: QUEUE_QUERY,
      variables() {
        return {
          site_id: parseInt(this.$route.params.site_id, 10)
        }
      },
      debounce: 3000,
      fetchPolicy: 'no-cache',
      result() {
        this.poll()
      }
    },
    site: {
      query: SITE_QUERY,
      variables() {
        return {
          site_id: this.$route.params.site_id,
          score: process.env.VUE_APP_SCORE_HIGH
        }
      },
      update(data) {
        if(isEmpty(data.site) || !data.site.activated_at) {
          localStorage.removeItem('site_id')
          const $vm = this
          console.error('Site ID Invalid', data)
          this.$bvModal.msgBoxOk(!data.site ? 'You do not have access to this site' : 'This site is disabled').then(() => {
            if($vm.isDuda) {
              _dAPI.refresh()
            } else {
              $vm.$router.push({name: 'index'})
            }
          })
          _zyppy.trackOnce('Site ID Invalid', data.site)
        }
        return data.site
      }
    },
    gsc_site_traffic: {
      query: GSC_SITE_TRAFFIC_QUERY,
      variables() {
        return {
          site_id: parseInt(this.$route.params.site_id),
          date: new Date().toDateString()
        }
      },
      // fetchPolicy: 'cache-first',
    },
  },

  computed: {
    importantQueues() {
      return this.queue.queues.filter(e => e.type != 'traffic')
    },
    queueCompleted() {
      return sumBy(this.importantQueues, 'complete')
    },
    queueDepth() {
      return sumBy(this.importantQueues, 'queue')
    },
    lastProcessingDate() {
      const date = get(this.site, 'last_processing[0].data.started_at')
      return date ? new Date(toNumber(date)) : null
    },
    isProcessing() {
      return !this.site.id ? null : (this.lastQueueDate && this.lastProcessingDate < this.lastQueueDate)
    },
    pollSpeed() {
      return this.isProcessing || this.$route.query.new ? 3000 : 10000
    },
    siteTraffic() {
      if(this.$apollo.queries.gsc_site_traffic.loading) {
        return {loading: true}
      }
      if(isEmpty(this.gsc_site_traffic)) {
        throw new Error('missing gsc_site_traffic')
      }
      const summary = {
        days: this.gsc_site_traffic.this_month.length,
        this_month: sumBy(this.gsc_site_traffic.this_month, 'clicks'),
        last_month: sumBy(this.gsc_site_traffic.last_month, 'clicks'),
        last_year: sumBy(this.gsc_site_traffic.last_year, 'clicks'),
      }
      summary.difference_month = summary.this_month - summary.last_month
      summary.difference_year = summary.this_month - summary.last_year
      summary.delta_month = this.$options.filters.delta(summary.this_month, summary.last_month)
      summary.delta_year = this.$options.filters.delta(summary.this_month, summary.last_year)
      return summary
    },
    documentTitle() {
      const title = `${this.isProcessing ? '(Crawling) ' : ''}${get(this.site, 'url') ? this.site.url + ' -  ' : ''}SiteSeer`
      if(title != document.title) {
        document.title = title
      }
      return title
    }
  },

  created() {
    const $vm = this
    window.addEventListener('visibilitychange', () => setTimeout($vm.poll, 10))
  },

  watch: {
    isProcessing: {
      handler: async function(to, from) {
        await this.$nextTick()
        if(to) {
          this.$bvToast.show('is-processing')
          const favicon = document.querySelector('link[rel="icon"]')
          if(favicon) {
            favicon.href = to ? '/favicon-crawling.ico' : '/favicon.ico'
          }
        } else if(from && !this.changedSite) {
          if(this.$route.query.new) {
            this.$router.replace({query: omit(this.$route.query, 'new')})
          }
          this.refresh()
          this.$bvToast.hide('is-processing')
          this.$bvToast.toast('🎉', {
            title: 'Crawl complete',
            bodyClass: 'd-none',
            variant: 'success',
          })
        }
        this.changedSite = false

      }
    },
    '$route.params.site_id': function(site_id, old_site_id) {
      if(old_site_id && old_site_id != site_id) {
        this.changedSite = true
      }
      if(site_id) {
        localStorage.site_id = site_id
      }
    },
  },

  methods: {
    refresh() {
      this.$apollo.queries.queue.refetch()
      this.$apollo.queries.site.refresh()
    },
    async poll() {
      if(this.queue.queues.length) {
        for(const {started_at} of this.queue.queues) {
          const date = new Date(started_at)
          if(date > this.lastQueueDate) {
            this.lastQueueDate = date
          }
        }
      } else {
        this.lastQueueDate = null
      }
      if(document.visibilityState != 'hidden') {
        this.$apollo.queries.queue.startPolling(this.pollSpeed)
      } else {
        this.$apollo.queries.queue.stopPolling()
      }
      if(this.queue.tracks.length) {
        for(let {event, properties, offline, time} of this.queue.tracks) {
          properties = JSON.parse(properties)
          if(time) {
            properties.time = time // Mixpanel
            properties.timestamp_micros = time // GA
          }
          _zyppy.track(event, properties)
        }
      }
    }
  }
}
</script>
